import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Card, CardContent } from './ui/Card';
import { ScanIcon, PaletteIcon, SparklesIcon, SofaIcon, CameraIcon, WandIcon } from 'lucide-react';
import { Button } from './ui/Button';
export function InteriorAISection() {
  const revealRef = useScrollReveal();
  const features = [{
    icon: <CameraIcon className="w-6 h-6" data-id="element-283" data-mp="3gq7mujofjd9f" />,
    title: 'Odfotíte Izbu',
    desc: 'Stačí jedna fotka vášho priestoru. AI rozpozná rozmery, nábytok, osvetlenie a štýl.',
    color: 'text-blue-500',
    bg: 'bg-blue-50'
  }, {
    icon: <SparklesIcon className="w-6 h-6" data-id="element-284" data-mp="2syiytfin5aya" />,
    title: 'AI Navrhne Dizajn',
    desc: 'Gemini vygeneruje 5 návrhov v rôznych štýloch — od minimalizmu po luxus. Za sekúnd.',
    color: 'text-purple-500',
    bg: 'bg-purple-50'
  }, {
    icon: <PaletteIcon className="w-6 h-6" data-id="element-285" data-mp="8axp1nrkou7g" />,
    title: 'Prispôsobte Farby',
    desc: 'Interaktívne meňte farby stien, podláh a doplnkov. AI zachová harmóniu celého priestoru.',
    color: 'text-amber-500',
    bg: 'bg-amber-50'
  }, {
    icon: <SofaIcon className="w-6 h-6" data-id="element-286" data-mp="1bbb8xsmk0zgp" />,
    title: 'Virtuálny Nábytok',
    desc: 'Vložte reálne produkty z katalógov priamo do vášho priestoru. Vidíte ako vyzerajú ešte pred kúpou.',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50'
  }, {
    icon: <ScanIcon className="w-6 h-6" data-id="element-287" data-mp="xa8p7mj1818y" />,
    title: '3D Vizualizácia',
    desc: 'Prechádzajte sa po novom interiéri vo virtuálnej realite. Zažite priestor ešte pred realizáciou.',
    color: 'text-rose-500',
    bg: 'bg-rose-50'
  }, {
    icon: <WandIcon className="w-6 h-6" data-id="element-288" data-mp="nkyvy3cgccj9" />,
    title: 'Rozpočet & Nákup',
    desc: 'AI vypočíta náklady a prepojí vás priamo s dodávateľmi. Od návrhu po realizáciu na jedno kliknutie.',
    color: 'text-indigo-500',
    bg: 'bg-indigo-50'
  }];
  const styles = [{
    name: 'Minimalizmus',
    image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=400&h=300&fit=crop&crop=center'
  }, {
    name: 'Škandinávsky',
    image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop&crop=center'
  }, {
    name: 'Industriálny',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop&crop=center'
  }, {
    name: 'Luxusný',
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400&h=300&fit=crop&crop=center'
  }];
  return <section id="interiorai" ref={revealRef} className="py-36 px-4 sm:px-6 lg:px-8 overflow-hidden" data-id="element-289" data-mp="mr7yu7t28rpx">
      <div className="max-w-7xl mx-auto" data-id="element-290" data-mp="dwti00svt94p">
        {/* Header */}
        <div className="reveal text-center mb-10" data-id="element-291" data-mp="duyxgnlivj15">
          <p className="text-xs font-semibold tracking-[0.25em] text-apple-gray uppercase mb-6" data-id="element-292" data-mp="11me7l2jjj4my">
            NOVÝ PRODUKT
          </p>
          <h2 className="text-4xl md:text-7xl font-extrabold tracking-tight text-apple-black mb-6 leading-tight" data-id="element-293" data-mp="16ep55yqt6ecc">
            nexify-studio.tech
            <br data-id="element-294" data-mp="1br4apbqokesr" />
            <span className="text-gradient-blue" data-id="element-295" data-mp="1bpjgxoc6wzvy">InteriorAI</span>
          </h2>
          <p className="text-xl md:text-2xl text-apple-gray font-light max-w-3xl mx-auto leading-relaxed" data-id="element-296" data-mp="1piiuvlwbqm1e">
            Odfotíte izbu. AI navrhne interiér. Vy si vyberiete štýl. Revolúcia
            v interiérovom dizajne poháňaná Gemini multimodálnou AI.
          </p>
        </div>

        {/* Hero Image */}
        <div className="reveal reveal-delay-1 max-w-5xl mx-auto mb-28" data-id="element-297" data-mp="ssiadz2zmrpw">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl" data-id="element-298" data-mp="16rhuctfk2ifq">
            <img src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1200&h=600&fit=crop&crop=center" alt="InteriorAI vizualizácia" className="w-full h-64 md:h-[480px] object-cover" data-id="element-299" data-mp="16pey3rnt7t2k" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-300" data-mp="2d6ikgihwg6ee" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12" data-id="element-301" data-mp="mrrp9kthq4lw">
              <div className="flex flex-wrap gap-3 mb-4" data-id="element-302" data-mp="rorkf0osxmy8">
                <span className="bg-white/20 backdrop-blur-md text-white text-xs font-semibold px-4 py-1.5 rounded-full border border-white/20" data-id="element-303" data-mp="rre3nxl21qwa">
                  Gemini 2.0
                </span>
                <span className="bg-white/20 backdrop-blur-md text-white text-xs font-semibold px-4 py-1.5 rounded-full border border-white/20" data-id="element-304" data-mp="10o4yy7rs91pt">
                  PWA Ready
                </span>
                <span className="bg-white/20 backdrop-blur-md text-white text-xs font-semibold px-4 py-1.5 rounded-full border border-white/20" data-id="element-305" data-mp="1amc8i5jqshhs">
                  Real-time Rendering
                </span>
              </div>
              <p className="text-white/80 text-sm md:text-base font-light max-w-xl" data-id="element-306" data-mp="1og3l5msrshhq">
                AI analyzuje váš priestor a za sekundy vytvorí fotorealistické
                návrhy interiéru v ľubovoľnom štýle.
              </p>
            </div>
          </div>
        </div>

        {/* Interactive Device Showcase */}
        <div className="reveal reveal-delay-2 relative w-full max-w-5xl mx-auto h-auto md:h-[420px] mb-28 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-6" data-id="element-307" data-mp="38xl30ty2sa8o">
          {/* Phone — Room Scanner */}
          <div className="animate-float-delayed w-48 md:w-44 h-80 bg-white rounded-[2rem] border-4 border-gray-100 shadow-2xl flex flex-col items-center relative z-20 md:transform md:-rotate-6 md:translate-x-4 card-hover-premium overflow-hidden" data-id="element-308" data-mp="2zyhjxpywruu6">
            <div className="w-1/2 h-1 bg-gray-200 rounded-full absolute top-4 z-10" data-id="element-309" data-mp="3nv54hqvrjna3"></div>
            <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&h=300&fit=crop&crop=center" alt="InteriorAI Room Scan" className="w-full h-full object-cover absolute inset-0 opacity-30" data-id="element-310" data-mp="3nssgavzewgol" />
            <div className="relative z-10 flex flex-col items-center justify-center h-full gap-2" data-id="element-311" data-mp="fdqvdryoh2xt">
              <CameraIcon className="w-8 h-8 text-apple-blue mb-1" data-id="element-312" data-mp="yc3rjjin5294" />
              <span className="text-apple-black font-bold text-sm" data-id="element-313" data-mp="13awx8proroyq">
                InteriorAI
              </span>
              <span className="text-apple-gray text-xs font-medium" data-id="element-314" data-mp="pih025lgul5q">
                Room Scanner
              </span>
              <div className="flex gap-1 mt-2" data-id="element-315" data-mp="tj0x7961horv">
                <div className="w-2 h-2 rounded-full bg-apple-blue animate-pulse" data-id="element-316" data-mp="fkjdrnn62yfr"></div>
                <div className="w-2 h-2 rounded-full bg-purple-500" data-id="element-317" data-mp="kjvz23ye3xg8"></div>
              </div>
              <span className="text-[10px] text-apple-gray mt-3 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full" data-id="element-318" data-mp="6ke6relvy224">
                📸 Scan & Design
              </span>
            </div>
          </div>

          {/* Desktop — AI Dashboard */}
          <div className="animate-float w-full max-w-sm md:max-w-none md:w-[420px] h-60 md:h-80 bg-white rounded-xl md:rounded-[2rem] border-4 border-gray-100 shadow-2xl flex flex-col relative z-30 card-hover-premium overflow-hidden" data-id="element-319" data-mp="20zqicosuz75g">
            <div className="w-full h-7 bg-gray-50 border-b border-gray-100 rounded-t-lg md:rounded-t-[1.75rem] flex items-center px-4 gap-1.5 flex-shrink-0" data-id="element-320" data-mp="3pfynlbctv36b">
              <div className="w-2 h-2 rounded-full bg-red-400" data-id="element-321" data-mp="33i9qsmmszit"></div>
              <div className="w-2 h-2 rounded-full bg-amber-400" data-id="element-322" data-mp="3uhe9f5mtxdpr"></div>
              <div className="w-2 h-2 rounded-full bg-green-400" data-id="element-323" data-mp="3ufbce1n9ekbj"></div>
              <span className="text-[10px] text-apple-gray ml-auto font-mono" data-id="element-324" data-mp="cz37geko3dmr">
                interiorai.app
              </span>
            </div>
            <div className="flex-1 flex" data-id="element-325" data-mp="m12qtdj5mtsv">
              {/* Sidebar */}
              <div className="w-20 md:w-24 bg-gray-50 border-r border-gray-100 p-3 flex flex-col gap-2" data-id="element-326" data-mp="lz8egnly8a0e">
                <div className="w-full h-2 bg-apple-blue/20 rounded-full" data-id="element-327" data-mp="h0f24yieep1t"></div>
                <div className="w-3/4 h-2 bg-gray-200 rounded-full" data-id="element-328" data-mp="2nsy05mhxum7x"></div>
                <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-329" data-mp="2iunzwxhcwvfg"></div>
                <div className="w-2/3 h-2 bg-purple-200 rounded-full" data-id="element-330" data-mp="1xygjwopx9cru"></div>
                <div className="mt-auto" data-id="element-331" data-mp="21zijj6nr3quc">
                  <SparklesIcon className="w-4 h-4 text-apple-blue mx-auto" data-id="element-332" data-mp="21ygws4xdhfws" />
                </div>
              </div>
              {/* Main content */}
              <div className="flex-1 p-4 flex flex-col items-center justify-center gap-3" data-id="element-333" data-mp="26sl6gvy0w21k">
                <SparklesIcon className="w-8 h-8 text-apple-blue" data-id="element-334" data-mp="2br65wt5gahoy" />
                <span className="text-apple-black font-bold text-base" data-id="element-335" data-mp="1f0nf1gvqmsrt">
                  InteriorAI Dashboard
                </span>
                <div className="flex gap-2" data-id="element-336" data-mp="1j2hanar2t6l4">
                  <div className="w-3 h-3 rounded-full bg-apple-blue" data-id="element-337" data-mp="1nypmae62ehnj"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-338" data-mp="1szdbqvenaqfn"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-500" data-id="element-339" data-mp="1swhgjeu6mfuw"></div>
                </div>
                <div className="flex gap-1 mt-1" data-id="element-340" data-mp="2zha6ernce1bq">
                  <div className="h-6 w-8 bg-blue-100 rounded" data-id="element-341" data-mp="34cxzhi4y3emi"></div>
                  <div className="h-6 w-8 bg-emerald-100 rounded" data-id="element-342" data-mp="sqdxljqed275"></div>
                  <div className="h-6 w-8 bg-amber-100 rounded" data-id="element-343" data-mp="sshzjw4qjr4s"></div>
                  <div className="h-6 w-8 bg-rose-100 rounded" data-id="element-344" data-mp="ns2ojb7xevpz"></div>
                </div>
                <span className="text-[10px] text-apple-gray mt-1" data-id="element-345" data-mp="izja6c1ezwk4">
                  5 návrhov vygenerovaných
                </span>
              </div>
            </div>
          </div>

          {/* Tablet — 3D Visualization */}
          <div className="animate-float-slow w-64 md:w-56 h-72 md:h-80 bg-white rounded-[1.5rem] md:rounded-[2rem] border-4 border-gray-100 shadow-2xl flex flex-col items-center relative z-10 md:transform md:rotate-6 md:-translate-x-4 card-hover-premium overflow-hidden" data-id="element-346" data-mp="9y2y7zuybmds">
            <div className="w-1 h-1/4 bg-gray-200 rounded-full absolute left-2 z-10" data-id="element-347" data-mp="3r9ksh7kkvil4"></div>
            <img src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=300&h=400&fit=crop&crop=center" alt="InteriorAI 3D View" className="w-full h-full object-cover absolute inset-0 opacity-25" data-id="element-348" data-mp="1uey394qi649y" />
            <div className="relative z-10 flex flex-col items-center justify-center h-full gap-2" data-id="element-349" data-mp="yxdq6slntnr2">
              <ScanIcon className="w-8 h-8 text-rose-500 mb-1" data-id="element-350" data-mp="1du4h0dh9pr52" />
              <span className="text-apple-black font-bold text-sm" data-id="element-351" data-mp="1dsbkvs4a6pbb">
                InteriorAI
              </span>
              <span className="text-apple-gray text-xs font-medium" data-id="element-352" data-mp="18tgp7pl7t69s">
                3D Vizualizácia
              </span>
              <div className="flex gap-1.5 mt-2" data-id="element-353" data-mp="g75swr4n5ris">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-500" data-id="element-354" data-mp="g8gvinmcot8b"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-apple-blue" data-id="element-355" data-mp="uwbqybrqtbp3"></div>
              </div>
              <span className="text-[10px] text-apple-gray mt-3 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full" data-id="element-356" data-mp="q3san71o82gl">
                🏠 Walk-through
              </span>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="reveal mb-28" data-id="element-357" data-mp="3tjpmrlje7khk">
          <h3 className="text-3xl md:text-4xl font-bold text-apple-black text-center mb-16 tracking-tight" data-id="element-358" data-mp="29386cx5e3uil">
            Ako to funguje
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-359" data-mp="2dwaw0u71qjti">
            {features.map((feature, i) => <Card key={i} className="border-gray-100 rounded-2xl bg-white/80 backdrop-blur-sm shadow-sm card-hover-premium" data-id="element-360" data-mp="24455zq0cucgy" data-mpi={i}>
                <CardContent className="p-8" data-id="element-361" data-mp="2rs6dqwpplovb">
                  <div className={`w-12 h-12 ${feature.bg} rounded-xl flex items-center justify-center mb-5`} data-id="element-362" data-mp="2ws12atm0i2d7">
                    <div className={feature.color} data-id="element-363" data-mp="2ivnjnh5qrux5">{feature.icon}</div>
                  </div>
                  <h4 className="text-lg font-bold text-apple-black mb-3" data-id="element-364" data-mp="2mwzatr49ko7g">
                    {feature.title}
                  </h4>
                  <p className="text-apple-gray font-light leading-relaxed text-sm" data-id="element-365" data-mp="1bg9486vxhudb">
                    {feature.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Style Gallery */}
        <div className="reveal mb-28" data-id="element-366" data-mp="395ei94zvg72t">
          <h3 className="text-3xl md:text-4xl font-bold text-apple-black text-center mb-6 tracking-tight" data-id="element-367" data-mp="34btjhaqgf92k">
            Vyberte si štýl
          </h3>
          <p className="text-apple-gray text-center font-light mb-14 max-w-2xl mx-auto" data-id="element-368" data-mp="2zbfmcnqe5jjb">
            AI rozumie desiatkam interiérových štýlov. Stačí povedať alebo
            ukázať, čo sa vám páči.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-369" data-mp="2qf6lr5qogevl">
            {styles.map((style, i) => <div key={i} className="group relative rounded-2xl overflow-hidden shadow-md card-hover-premium cursor-pointer" data-id="element-370" data-mp="2gew8xm8k19gy" data-mpi={i}>
                <img src={style.image} alt={style.name} className="w-full h-48 md:h-56 object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-371" data-mp="jk04mquz38ev" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-372" data-mp="r5df6u9zts0i" />
                <span className="absolute bottom-4 left-4 text-white font-bold text-sm md:text-base" data-id="element-373" data-mp="vw3k66eibsuo">
                  {style.name}
                </span>
              </div>)}
          </div>
        </div>

        {/* CTA */}
        <div className="reveal text-center" data-id="element-374" data-mp="46qwxmzztblu">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-12 md:p-16 border border-gray-200" data-id="element-375" data-mp="8717lcyqcq71">
            <h3 className="text-3xl md:text-5xl font-extrabold tracking-tight text-apple-black mb-6" data-id="element-376" data-mp="d5ksda1l7vxt">
              Chcete InteriorAI pre váš biznis?
            </h3>
            <p className="text-lg text-apple-gray font-light max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-377" data-mp="i2ug8l9bvzjj">
              Či ste realitná kancelária, dizajnérske štúdio alebo e-shop s
              nábytkom — InteriorAI sa prispôsobí vašim potrebám.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-378" data-mp="32i3rcqq949qn">
              <a href="https://interiorai.nexify-studio.tech" target="_blank" rel="noopener noreferrer" data-id="element-379" data-mp="2xj96zkcmyd4f">
                <Button size="large" className="!bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-full !px-10 !py-4 !font-bold w-full sm:w-auto btn-hover-premium !border-none" data-id="element-380" data-mp="36j5uhinah5xw">
                  Dohodnúť Demo
                </Button>
              </a>
              <a href="https://gruppa.cloud/privacy/" target="_blank" rel="noopener noreferrer" data-id="element-381" data-mp="3u8qcnzbmbz64">
                <Button size="large" className="!bg-white hover:!bg-gray-50 !text-apple-black !border-2 !border-gray-200 !rounded-full !px-10 !py-4 !font-bold w-full sm:w-auto card-hover-premium" data-id="element-382" data-mp="3q998d97xh82j">
                  Ochrana súkromia
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}